/**
 * BestSendTimePanel — #14 Best-time-to-send widget op de Outreach-pagina's.
 * Heatmap (weekdag × uurblok) per kanaal met reply-rate-intensiteit + de
 * aanbevolen verzendslot. Bij pacing "throttled" schuift de planner sends
 * automatisch naar dit blok zodra er genoeg eigen data is (basis "learned").
 * Data: GET /outreach/analytics/best-send-time
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import i18n from "i18next";
import { Clock, ChevronDown, ChevronUp, Sparkles } from "lucide-react";

const CH_META = {
    linkedin: { label: "LinkedIn", color: "#0a66c2" },
    email: { label: "E-mail", color: "#e04b26" },
};

function cellBg(cell, best) {
    // Theme-aware: neutrale grijstint voor lege cellen en een donkere teal
    // voor gevulde — leesbaar op zowel het donkere als het lichte thema.
    if (!cell.sent) return "rgba(127,127,127,0.12)";
    const r = cell.reply_rate || 0;
    const alpha = 0.25 + Math.min(r, 0.5) * 1.2;
    return best ? `rgba(34,197,94,${Math.max(alpha, 0.55)})` : `rgba(13,148,136,${alpha})`;
}

export default function BestSendTimePanel() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [channel, setChannel] = useState("linkedin");

    useEffect(() => {
        api.get("/outreach/analytics/best-send-time").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) return null;
    const chKeys = Object.keys(data.channels || {}).filter((k) => CH_META[k]);
    if (!chKeys.length) return null;
    const active = data.channels[channel] ? channel : chKeys[0];
    const ch = data.channels[active];
    const rec = ch.recommendation;
    const learned = rec.basis === "learned";

    return (
        <div className="mb-4 rounded-xl border border-white/10 bg-[var(--jr-surface)]" data-testid="best-send-time-panel">
            <button onClick={() => setOpen(!open)} data-testid="bst-toggle"
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <Clock size={15} className="text-[#14e0c8]" />
                <span className="text-sm font-medium text-white">{i18n.t("bst.title")}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full border font-mono"
                    style={{ color: learned ? "#22c55e" : "var(--jr-text-3)", borderColor: learned ? "#22c55e55" : "rgba(255,255,255,0.15)" }}
                    data-testid="bst-recommendation-chip">
                    {rec.day_label} {rec.block_label}
                    {learned && rec.reply_rate != null ? ` · ${Math.round(rec.reply_rate * 100)}%` : ""}
                </span>
                <span className="text-[10px] text-[var(--jr-text-3)]">
                    {learned ? i18n.t("bst.basis_learned", { n: ch.total_sent }) : i18n.t("bst.basis_default")}
                </span>
                <span className="ml-auto text-[var(--jr-text-3)]">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            {open && (
                <div className="px-4 pb-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        {chKeys.map((k) => (
                            <button key={k} onClick={() => setChannel(k)} data-testid={`bst-channel-${k}`}
                                className={`text-[11px] px-2.5 py-1 rounded-full border ${active === k
                                    ? "text-white border-white/40 bg-white/10"
                                    : "text-[var(--jr-text-3)] border-white/10 hover:text-white"}`}>
                                <span style={{ color: CH_META[k].color }}>●</span> {CH_META[k].label}
                            </button>
                        ))}
                        {learned && (
                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-[#22c55e]">
                                <Sparkles size={11} /> {i18n.t("bst.auto_applied")}
                            </span>
                        )}
                    </div>
                    <div className="grid gap-1" style={{ gridTemplateColumns: `44px repeat(${data.block_labels.length}, 1fr)` }}>
                        <div />
                        {data.block_labels.map((b) => (
                            <div key={b} className="text-[9px] font-mono text-center text-[var(--jr-text-3)]">{b}</div>
                        ))}
                        {data.day_labels.map((d, di) => (
                            <React.Fragment key={d}>
                                <div className="text-[10px] font-mono text-[var(--jr-text-2)] flex items-center">{d}</div>
                                {ch.heatmap[di].map((cell, bi) => {
                                    const isBest = learned && rec.day === di && rec.block === bi;
                                    return (
                                        <div key={bi} data-testid={`bst-cell-${di}-${bi}`}
                                            title={cell.sent
                                                ? `${cell.sent} verzonden · ${cell.replied} beantwoord${cell.reply_rate != null ? ` (${Math.round(cell.reply_rate * 100)}%)` : ""}`
                                                : i18n.t("bst.no_data")}
                                            className={`h-7 rounded flex items-center justify-center text-[9px] font-mono ${isBest ? "ring-1 ring-[#22c55e] text-black font-semibold" : "text-[var(--jr-text-2)]"}`}
                                            style={{ background: cellBg(cell, isBest) }}>
                                            {cell.sent ? `${cell.sent}` : ""}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="mt-2 text-[10px] text-[var(--jr-text-3)]">
                        {i18n.t("bst.legend", { min: data.min_sample })} · {data.timezone}
                    </p>
                </div>
            )}
        </div>
    );
}
